var _hooksRemoveReadingHistoryArticle, _hooksUseReadingHistoryWithArticleQuery;
import { useAsync, useMap, useMountEffect, useUpdateEffect } from "@react-hookz/web";
import axios from "axios";
import { differenceInMinutes } from "date-fns";
import difference from "lodash/difference";
import uniqBy from "lodash/uniqBy";
import { useCallback, useEffect, useMemo } from "react";
import { fetchQuery, graphql, readInlineData, useRelayEnvironment } from "react-relay";
import { scmpPlus, useSdkContext } from "~/components/contexts";
import { useFallbackStorageValue } from "~/lib/hooks";
import { notEmpty } from "~/lib/utils";
import { create, fetch, removeAllAtDate, removeByUuidAtDate } from "./apis";
import { LocalStorageKeys, ReadingHistoryItemSize } from "./consts";
import { formatGroupDate } from "./helpers";
export const useReadingHistorySetup = () => {
  const {
    currentArticleUuid,
    user
  } = useSdkContext();
  const {
    createReadingHistory,
    fetchReadingHistory,
    lastSyncedDate,
    resetLocalReadingHistory,
    syncReadingHistory
  } = useReadingHistoryOperations();

  // Fetch remote data if not yet fetched
  useMountEffect(async () => {
    await fetchReadingHistory();
  });

  // Clear local storage for anonymous user
  useEffect(() => {
    if (user) return;
    resetLocalReadingHistory();
  }, [resetLocalReadingHistory, user]);

  // Create reading history for current article
  useEffect(() => {
    if (!currentArticleUuid) return;
    createReadingHistory(currentArticleUuid);
  }, [createReadingHistory, currentArticleUuid]);

  // Sync pending reading history periodically
  useUpdateEffect(() => {
    if (differenceInMinutes(new Date(), new Date(lastSyncedDate !== null && lastSyncedDate !== void 0 ? lastSyncedDate : 0)) < 1) return;
    void syncReadingHistory();
  }, [syncReadingHistory, currentArticleUuid, lastSyncedDate]);
};
export const useReadingHistory = () => {
  const {
    user
  } = useSdkContext();
  const {
    pendingReadingHistory,
    syncedReadingHistory
  } = useReadingHistoryLocalStorage();
  const readingHistory = useMemo(() => {
    if (!user) return [];
    const merged = [...(pendingReadingHistory !== null && pendingReadingHistory !== void 0 ? pendingReadingHistory : []), ...(syncedReadingHistory !== null && syncedReadingHistory !== void 0 ? syncedReadingHistory : [])].sort((a, b) => b.createdAt - a.createdAt);
    return merged;
  }, [pendingReadingHistory, syncedReadingHistory, user]);
  return {
    readingHistory
  };
};
export const useReadingHistoryOperations = () => {
  const {
    config,
    handleTrackGA,
    handleTrackGA4,
    user
  } = useSdkContext();
  const axiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: config.readingHistory.host,
      headers: {
        apikey: config.readingHistory.apiKey
      }
    });
    return instance;
  }, [config]);
  const {
    lastSyncedDate,
    pendingReadingHistory,
    setLastSyncedDate,
    setPendingReadingHistory,
    setSyncedReadingHistory
  } = useReadingHistoryLocalStorage();
  const fetchReadingHistory = useCallback(async () => {
    if (!user || lastSyncedDate) return;
    const remoteData = await fetch(axiosInstance, user.uuid, ReadingHistoryItemSize);
    setSyncedReadingHistory(remoteData.map(item => ({
      createdAt: new Date(item.createdAt).getTime(),
      entityUuid: item.entityUuid
    })));
    setLastSyncedDate(Date.now());
  }, [axiosInstance, lastSyncedDate, setLastSyncedDate, setSyncedReadingHistory, user]);
  const syncReadingHistory = useCallback(async () => {
    if (!user) return;
    if (!pendingReadingHistory || pendingReadingHistory.length === 0) return;
    const pendingItems = pendingReadingHistory.map(item => ({
      createdAt: new Date(item.createdAt),
      entityUuid: item.entityUuid,
      expiresAt: new Date(item.createdAt)
    }));
    await create(axiosInstance, user.uuid, pendingItems);
    try {
      const remoteData = await fetch(axiosInstance, user.uuid, ReadingHistoryItemSize);
      setSyncedReadingHistory(remoteData.map(item => ({
        createdAt: new Date(item.createdAt).getTime(),
        entityUuid: item.entityUuid
      })));
      setLastSyncedDate(Date.now());
    } catch {
      setSyncedReadingHistory(history => [...(pendingReadingHistory !== null && pendingReadingHistory !== void 0 ? pendingReadingHistory : []), ...(history !== null && history !== void 0 ? history : [])]);
    } finally {
      setPendingReadingHistory([]);
    }
  }, [axiosInstance, pendingReadingHistory, setLastSyncedDate, setPendingReadingHistory, setSyncedReadingHistory, user]);
  const createReadingHistory = useCallback(entityUuid => {
    if (!user) return;
    const newItem = {
      createdAt: Date.now(),
      entityUuid
    };
    setPendingReadingHistory(history => {
      const data = uniqBy([newItem, ...(history !== null && history !== void 0 ? history : [])], item => `${item.entityUuid}-${formatGroupDate(item.createdAt)}`);
      return data;
    });
    setSyncedReadingHistory(history => [...(history !== null && history !== void 0 ? history : [])].filter(item => !(item.entityUuid === newItem.entityUuid && formatGroupDate(item.createdAt) === formatGroupDate(newItem.createdAt))));
  }, [setPendingReadingHistory, setSyncedReadingHistory, user]);
  const clearReadingHistory = useCallback(async (groupDate, articleEntityIds) => {
    if (!user) return;
    const filter = history => [...(history !== null && history !== void 0 ? history : [])].filter(item => formatGroupDate(item.createdAt) !== groupDate);
    setPendingReadingHistory(filter);
    setSyncedReadingHistory(filter);
    await removeAllAtDate(axiosInstance, groupDate, user.uuid);
    handleTrackGA({
      action: "My Account/Overlay/Reading History/Clear",
      category: "My Account",
      label: ""
    });
    handleTrackGA4({
      action: "click",
      category: "readinghistory",
      customized_parameters: {
        action_type: "clear",
        article_id: articleEntityIds !== null && articleEntityIds !== void 0 ? articleEntityIds : ""
      },
      subcategory: ""
    });
  }, [axiosInstance, handleTrackGA, handleTrackGA4, setPendingReadingHistory, setSyncedReadingHistory, user]);
  const removeReadingHistory = useCallback(async (reference, createdAt) => {
    if (!user) return;
    const {
      entityId,
      entityUuid
    } = readInlineData(_hooksRemoveReadingHistoryArticle !== void 0 ? _hooksRemoveReadingHistoryArticle : (_hooksRemoveReadingHistoryArticle = require("../../../../queries/__generated__/hooksRemoveReadingHistoryArticle.graphql"), _hooksRemoveReadingHistoryArticle.hash && _hooksRemoveReadingHistoryArticle.hash !== "9cf9cb93788ef385dc0937902f020c9b" && console.error("The definition of 'hooksRemoveReadingHistoryArticle' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _hooksRemoveReadingHistoryArticle), reference);
    const filter = history => [...(history !== null && history !== void 0 ? history : [])].filter(item => !(item.entityUuid === entityUuid && item.createdAt === createdAt));
    setPendingReadingHistory(filter);
    setSyncedReadingHistory(filter);
    await removeByUuidAtDate(axiosInstance, entityUuid, formatGroupDate(createdAt));
    handleTrackGA({
      action: "My Account/Overlay/Reading History/Remove",
      category: "My Account",
      label: entityId
    });
    handleTrackGA4({
      action: "click",
      category: "readinghistory",
      customized_parameters: {
        action_type: "remove",
        article_id: entityId !== null && entityId !== void 0 ? entityId : ""
      },
      subcategory: ""
    });
  }, [axiosInstance, handleTrackGA, handleTrackGA4, setPendingReadingHistory, setSyncedReadingHistory, user]);
  const resetLocalReadingHistory = useCallback(() => {
    setPendingReadingHistory([]);
    setSyncedReadingHistory([]);
    setLastSyncedDate(0);
  }, [setLastSyncedDate, setPendingReadingHistory, setSyncedReadingHistory]);
  return {
    clearReadingHistory,
    createReadingHistory,
    fetchReadingHistory,
    lastSyncedDate,
    removeReadingHistory,
    resetLocalReadingHistory,
    syncReadingHistory
  };
};
const useReadingHistoryLocalStorage = () => {
  const {
    set: setPendingReadingHistory,
    value: pendingReadingHistory
  } = useFallbackStorageValue(LocalStorageKeys.pending);
  const {
    set: setSyncedReadingHistory,
    value: syncedReadingHistory
  } = useFallbackStorageValue(LocalStorageKeys.synced);
  const {
    set: setLastSyncedDate,
    value: lastSyncedDate
  } = useFallbackStorageValue(LocalStorageKeys.lastSyncedDate);
  return {
    lastSyncedDate,
    pendingReadingHistory,
    setLastSyncedDate,
    setPendingReadingHistory,
    setSyncedReadingHistory,
    syncedReadingHistory
  };
};
export const useReadingHistoryWithArticle = () => {
  const environment = useRelayEnvironment();
  const {
    readingHistory
  } = useReadingHistory();
  const articleNodeMap = useMap();
  const [state, actions] = useAsync(async () => {
    const newReadingHistoryEntityUuids = difference(readingHistory.map(item => item.entityUuid), [...articleNodeMap.keys()]);
    if (newReadingHistoryEntityUuids.length > 0) {
      const data = await fetchQuery(environment, _hooksUseReadingHistoryWithArticleQuery !== void 0 ? _hooksUseReadingHistoryWithArticleQuery : (_hooksUseReadingHistoryWithArticleQuery = require("../../../../queries/__generated__/hooksUseReadingHistoryWithArticleQuery.graphql"), _hooksUseReadingHistoryWithArticleQuery.hash && _hooksUseReadingHistoryWithArticleQuery.hash !== "cbde35cb215893f736ea456fd1a2e149" && console.error("The definition of 'hooksUseReadingHistoryWithArticleQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _hooksUseReadingHistoryWithArticleQuery), {
        entityUuids: [...newReadingHistoryEntityUuids],
        first: newReadingHistoryEntityUuids.length,
        scmpPlusPaywallTypeIds: [scmpPlus.article.paywallTypeId]
      }).toPromise();
      data === null || data === void 0 || data.articles.edges.forEach(({
        node
      }) => {
        articleNodeMap.set(node.entityUuid, node);
      });
    }
    const readingHistoryWithArticle = readingHistory.map(item => {
      const matched = articleNodeMap.get(item.entityUuid);
      if (!matched) return;
      return {
        ...item,
        article: matched
      };
    }).filter(notEmpty);
    return readingHistoryWithArticle;
  }, []);

  // Trigger when reading history updated
  useEffect(() => {
    void actions.execute();
  }, [actions, readingHistory]);
  return {
    state
  };
};